import { useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { errMsg, mediaSrc, useAdminApi } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const KINDS = [
  ["logo", "Logo", "image/*"],
  ["photo", "Doctor photo", "image/*"],
  ["hero", "Hero / landing-page image", "image/*"],
  ["legal_photo", "Legal Advisor photo", "image/*"],
  ["qr", "UPI QR code image", "image/*"],
  ["video", "Background video (mp4, up to 80 MB)", "video/mp4,video/webm,video/quicktime"],
  ["sound", "Background sound (mp3, wav, ogg or m4a, up to 20 MB)", "audio/mpeg,audio/wav,audio/ogg,audio/mp4,.mp3,.wav,.ogg,.m4a"],
];

const DEFAULT_LABEL = { video: "Using the default forest river video", sound: "Using the default river sound" };

function MediaCard({ kind, label, accept, url, onChanged }) {
  const call = useAdminApi();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await call("post", `/admin/media/${kind}`, fd);
      toast.success(`${label.split(" (")[0]} updated`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const reset = async () => {
    try {
      await call("delete", `/admin/media/${kind}`);
      toast.success(DEFAULT_LABEL[kind] ? "Back to default" : "Removed");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const src = mediaSrc(url);
  const preview = src || (kind === "sound" ? "/media/river.mp3" : null);
  return (
    <div className="space-y-3 rounded-2xl border border-white/10 p-4" data-testid={`media-card-${kind}`}>
      <p className="text-sm font-medium text-white">{label}</p>
      {kind === "sound" ? (
        <div className="flex h-40 flex-col items-center justify-center gap-3 rounded-xl bg-black/30 px-4">
          <span className="text-xs text-emerald-100/60">{src ? "Your uploaded sound" : DEFAULT_LABEL.sound}</span>
          <audio key={preview} src={preview} controls className="w-full" data-testid="sound-preview" />
        </div>
      ) : (
      <div className="flex h-40 items-center justify-center overflow-hidden rounded-xl bg-black/30">
        {src ? (kind === "video" ? <video src={src} muted autoPlay loop playsInline className="h-full w-full object-cover" /> : <img src={src} alt={label} className="h-full w-full object-contain" />)
          : <span className="text-xs text-emerald-100/50">{DEFAULT_LABEL[kind] || "Not uploaded yet"}</span>}
      </div>
      )}
      <div className="flex gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-emerald-400 px-4 py-2 text-sm text-emerald-950 hover:bg-emerald-300">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
          <input type="file" accept={accept} className="hidden" data-testid={`media-upload-${kind}`} disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        {src && <Button variant="ghost" size="sm" data-testid={`media-reset-${kind}`} onClick={reset}>{DEFAULT_LABEL[kind] ? "Use default" : "Remove"}</Button>}
      </div>
    </div>
  );
}

export default function MediaTab() {
  const { settings, reloadSettings } = useApp();
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="admin-media">
      {KINDS.map(([k, label, accept]) => (
        <MediaCard key={k} kind={k} label={label} accept={accept} url={settings?.media?.[k]} onChanged={reloadSettings} />
      ))}
    </div>
  );
}
